import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { useBookingModal } from '../context/BookingContext';
import { heroData } from '../data/mock';
import { trackEvent } from '../lib/track';

/**
 * StickyMobileBookBar
 *
 * Two floating action buttons pinned to the bottom of the mobile viewport:
 *  1. Book Now (teal, primary)  — opens the BookingModal
 *  2. WhatsApp (green circle)   — direct wa.me link
 *
 * On mobile the standalone <WhatsAppFAB /> is hidden (see App.css), so this
 * component owns BOTH mobile CTAs and there's no duplicate chat icon.
 *
 * Background is intentionally transparent so the buttons float above the page
 * content — modern floating-button aesthetic used by Function Health / Ro /
 * Restore.dental. Hidden while the hero is in view (which already has both
 * CTAs), and hidden on admin/thank-you/refer/link-in-bio.
 */
const HIDDEN_PATHS = [
  '/admin',
  '/thank-you',
  '/refer',
  '/link-in-bio',
];

const StickyMobileBookBar = () => {
  const location = useLocation();
  const { openBooking } = useBookingModal();
  const [heroInView, setHeroInView] = useState(true);

  const isHidden = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (location.pathname !== '/') {
      setHeroInView(false);
      return undefined;
    }
    const hero = document.querySelector('.hero-section');
    if (!hero) {
      setHeroInView(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setHeroInView(entry.isIntersecting && entry.intersectionRatio > 0.25);
      },
      { threshold: [0, 0.25, 0.5, 1] }
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, [location.pathname]);

  if (isHidden || heroInView) return null;

  const handleBook = () => {
    trackEvent('sticky_mobile_book_click', location.pathname);
    if (window.gtag) {
      window.gtag('event', 'sticky_mobile_book_click', {
        event_category: 'conversion',
        event_label: location.pathname,
      });
    }
    openBooking();
  };

  const handleWhatsApp = () => {
    trackEvent('sticky_mobile_whatsapp_click', location.pathname);
    if (window.gtag) {
      window.gtag('event', 'sticky_mobile_whatsapp_click', {
        event_category: 'conversion',
        event_label: location.pathname,
      });
    }
  };

  const waHref = `https://wa.me/${heroData.whatsappNumber}?text=${encodeURIComponent(
    "Hi! I'd like to enquire about TMD physiotherapy treatment."
  )}`;

  return (
    <div
      className="sticky-mobile-book-bar"
      data-testid="sticky-mobile-book-bar"
      role="region"
      aria-label="Book now or chat on WhatsApp"
    >
      <button
        type="button"
        className="sticky-mobile-book-bar-primary"
        onClick={handleBook}
        data-testid="sticky-mobile-book-btn"
      >
        <Calendar size={18} strokeWidth={2.2} />
        <span>Book Now</span>
      </button>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        className="sticky-mobile-book-bar-whatsapp"
        aria-label="Chat with us on WhatsApp"
        onClick={handleWhatsApp}
        data-testid="sticky-mobile-whatsapp-btn"
      >
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      </a>
    </div>
  );
};

export default StickyMobileBookBar;
